import React, { useState, useEffect } from "react";
import {
  DatePicker,
  Select,
  Button,
  Table,
  Modal,
  Form,
  Tag,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useFetchEmployeeOptions } from "../../AttendanceReport/hooks/useFetch";
import { onReportHeaderCell } from "../../AttendanceReport/utils";
import {
  useFetchTimeclockLogs,
  useAddTimeclockLog,
  useUpdateTimeclockLog,
  useDeleteTimeclockLog,
} from "../hooks/useFetch";

const ACTION_OPTIONS = [
  { value: "clock_in", label: "เข้างาน" },
  { value: "clock_out", label: "ออกงาน" },
];

const ActionTag = ({ action }) =>
  action === "clock_in" ? (
    <Tag color="green">เข้างาน</Tag>
  ) : (
    <Tag color="red">ออกงาน</Tag>
  );

// หน้า admin แก้ manual การตอกบัตร เข้างาน/ออกงาน (เพิ่ม/แก้/ลบ รายวัน)
const ManualTimeclockPage = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // { mode: 'add'|'edit', record }
  const [form] = Form.useForm();

  const { data: employeeOptions = [], isLoading: isLoadingEmployees } =
    useFetchEmployeeOptions();
  const { mutateAsync: fetchLogs, isLoading } = useFetchTimeclockLogs();
  const { mutateAsync: addLog, isLoading: isAdding } = useAddTimeclockLog();
  const { mutateAsync: updateLog, isLoading: isUpdating } = useUpdateTimeclockLog();
  const { mutateAsync: deleteLog } = useDeleteTimeclockLog();

  const handleSearch = async (date = selectedDate, userId = selectedUserId) => {
    try {
      const { result } = await fetchLogs({
        date: date.format("YYYY-MM-DD"),
        userId: userId || undefined,
      });
      setRows(result || []);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "ดึงรายการตอกบัตรไม่สำเร็จ"
      );
    }
  };

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shiftDay = (diff) => {
    const d = selectedDate.add(diff, "day");
    setSelectedDate(d);
    handleSearch(d);
  };

  // เปิด modal เพิ่ม — ตั้งเวลาเริ่มต้นเป็นวันที่เลือก ณ เวลาปัจจุบัน
  const openAdd = () => {
    setEditing({ mode: "add" });
    form.setFieldsValue({
      userId: selectedUserId || undefined,
      action: "clock_in",
      eventTime: selectedDate
        .hour(dayjs().hour())
        .minute(dayjs().minute())
        .second(0),
    });
  };

  // เปิด modal แก้
  const openEdit = (record) => {
    setEditing({ mode: "edit", record });
    form.setFieldsValue({
      userId: String(record.userId),
      action: record.action,
      eventTime: dayjs(record.event_time, "YYYY-MM-DD HH:mm:ss"),
    });
  };

  const closeModal = () => {
    setEditing(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const eventTime = values.eventTime.format("YYYY-MM-DD HH:mm:ss");
      if (editing.mode === "add") {
        await addLog({
          userId: values.userId,
          action: values.action,
          eventTime,
        });
        message.success("เพิ่มรายการตอกบัตรแล้ว");
      } else {
        await updateLog({
          id: editing.record.id,
          action: values.action,
          eventTime,
        });
        message.success("แก้ไขรายการตอกบัตรแล้ว");
      }
      closeModal();
      handleSearch();
    } catch (error) {
      if (error?.errorFields) return; // validation ของ form เอง
      message.error(
        error?.response?.data?.message || "บันทึกไม่สำเร็จ"
      );
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "ยืนยันการลบ",
      content: `ลบรายการ "${record.action === "clock_in" ? "เข้างาน" : "ออกงาน"}" ของ ${
        record.userName || "-"
      } เวลา ${record.event_time?.slice(11, 19)} ใช่หรือไม่?`,
      okText: "ลบ",
      okButtonProps: { danger: true },
      cancelText: "ยกเลิก",
      onOk: async () => {
        try {
          await deleteLog(record.id);
          message.success("ลบรายการแล้ว");
          handleSearch();
        } catch (error) {
          message.error(
            error?.response?.data?.message || "ลบไม่สำเร็จ"
          );
        }
      },
    });
  };

  const columns = [
    {
      title: "พนักงาน",
      dataIndex: "userName",
      key: "userName",
      onHeaderCell: onReportHeaderCell,
      render: (name) => <span style={{ fontWeight: 600 }}>{name || "-"}</span>,
    },
    {
      title: "ประเภท",
      dataIndex: "action",
      key: "action",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (action) => <ActionTag action={action} />,
    },
    {
      title: "เวลา",
      dataIndex: "event_time",
      key: "event_time",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (v) => (
        <span style={{ fontWeight: 600 }}>{v ? v.slice(11, 19) : "-"}</span>
      ),
      sorter: (a, b) => a.event_time.localeCompare(b.event_time),
    },
    {
      title: "จัดการ",
      key: "manage",
      align: "center",
      width: 160,
      onHeaderCell: onReportHeaderCell,
      render: (_, r) => (
        <>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(r)}
          >
            แก้
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(r)}
          >
            ลบ
          </Button>
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: 16, background: "#f5f5f5", minHeight: "100vh", marginLeft: 16 }}>
      <div style={{ marginBottom: 20 }}>
        <h1
          style={{
            margin: 0,
            fontSize: "2rem",
            fontWeight: "bold",
            color: "#212121",
            letterSpacing: "0.5px",
            lineHeight: 1.2,
          }}
        >
          แก้ manual เข้างาน / ออกงาน
        </h1>
        <p style={{ margin: "8px 0 0", color: "#666" }}>
          เพิ่ม / แก้ไข / ลบ เวลาตอกบัตรเข้างาน-ออกงานของพนักงาน (ใช้กรณีพนักงานลืมตอก หรือตอกผิด)
        </p>
      </div>

      <div style={{ background: "#fff", borderRadius: 10, padding: 20, marginBottom: 16 }}>
        <div className="flex gap-3 items-center flex-wrap">
          <Button icon={<LeftOutlined />} onClick={() => shiftDay(-1)} />
          <DatePicker
            value={selectedDate}
            onChange={(d) => {
              if (!d) return;
              setSelectedDate(d);
              handleSearch(d);
            }}
            format="DD/MM/YYYY"
            allowClear={false}
          />
          <Button
            icon={<RightOutlined />}
            disabled={selectedDate.isSame(dayjs(), "day")}
            onClick={() => shiftDay(1)}
          />
          <Select
            className="w-64"
            placeholder="พนักงานทั้งหมด"
            allowClear
            showSearch
            optionFilterProp="label"
            loading={isLoadingEmployees}
            options={employeeOptions}
            value={selectedUserId}
            onChange={(v) => {
              setSelectedUserId(v || null);
              handleSearch(selectedDate, v || null);
            }}
          />
          <Button type="primary" loading={isLoading} onClick={() => handleSearch()}>
            ค้นหา
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}
            style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}>
            เพิ่มรายการ
          </Button>
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: 10, padding: 16 }}>
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          bordered
          scroll={{ x: "max-content" }}
          pagination={{
            pageSize: 20,
            showSizeChanger: false,
            showTotal: (t) => `ทั้งหมด ${t} รายการ`,
          }}
        />
      </div>
      <style>{`.ant-table-tbody > tr:hover > td { background-color: #e8f4f8 !important; }`}</style>

      {/* Modal เพิ่ม/แก้ */}
      <Modal
        title={editing?.mode === "add" ? "เพิ่มรายการตอกบัตร" : "แก้ไขรายการตอกบัตร"}
        open={!!editing}
        onOk={handleSubmit}
        onCancel={closeModal}
        okText="บันทึก"
        cancelText="ยกเลิก"
        confirmLoading={isAdding || isUpdating}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="พนักงาน"
            name="userId"
            rules={[{ required: true, message: "เลือกพนักงาน" }]}
          >
            <Select
              placeholder="เลือกพนักงาน"
              showSearch
              optionFilterProp="label"
              loading={isLoadingEmployees}
              options={employeeOptions}
              disabled={editing?.mode === "edit"} // แก้ไขไม่ให้ย้ายคน
            />
          </Form.Item>
          <Form.Item
            label="ประเภท"
            name="action"
            rules={[{ required: true, message: "เลือกประเภท" }]}
          >
            <Select options={ACTION_OPTIONS} />
          </Form.Item>
          <Form.Item
            label="วันและเวลา"
            name="eventTime"
            rules={[{ required: true, message: "เลือกวันและเวลา" }]}
          >
            <DatePicker
              showTime={{ format: "HH:mm:ss" }}
              format="DD/MM/YYYY HH:mm:ss"
              style={{ width: "100%" }}
              allowClear={false}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ManualTimeclockPage;
